import { useEffect, useState } from "react";
import { Button } from '@/components/ui/button';
import { App, Select, Spin } from "antd";
import { useCalendar } from "@/api/hooks/useCalendar";
import { useProfiles } from "@/api/hooks/useProfile";
import { editCalendar } from "@/api/calendar";
import { useNavigate } from "react-router-dom";
import { Ban } from "lucide-react";

export function Calendar() {
  const [weekdayProfile, setWeekdayProfile] = useState<number | null>();
  const [weekendProfile, setWeekendProfile] = useState<number | null>();
  const { data: calendarApiData, isLoading: calendarLoading, refetch } = useCalendar();
  const { data: profilesApiData, isLoading: profilesLoading } = useProfiles();
  const { message } = App.useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (!calendarApiData?.[0]) return;
    setWeekdayProfile(calendarApiData[0].weekday_profile)
    setWeekendProfile(calendarApiData[0].weekend_profile)
  }, [calendarApiData]);

  const handleSave = async (profile: number | null | undefined, isWeekday: boolean) => {
    if (profile === undefined || !calendarApiData?.[0]?.id) return;

    try {
      // Send null directly to the API
      const payload = isWeekday
        ? { weekday_profile: profile }
        : { weekend_profile: profile };

      await editCalendar(calendarApiData[0].id, payload);
      refetch()
      message.success('Update Successfully');
    } catch (error) {
      message.error((error as Error).message);
    }
  };

  useEffect(() => {
    refetch()
  }, [navigate])

  if (calendarLoading || profilesLoading
    || !calendarApiData || !profilesApiData) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex justify-start items-center mb-2">
          <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="bg-white rounded-[10px] shadow-card p-2.5 space-y-4">
              <div className="h-5 w-20 bg-gray-200 rounded animate-pulse" />
              <div className="space-y-2">
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                <div className="h-10 w-full bg-gray-200 rounded-lg animate-pulse" />
              </div>
              <div className="h-9 w-full bg-gray-200 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Helper function to check if profiles are different (including null/undefined cases)
  const isProfileDifferent = (currentProfile: number | null | undefined, savedProfile: number | undefined) => {
    // If current is null and saved is undefined or 0, they're equivalent (both represent "no profile")
    if (currentProfile === null && (!savedProfile || savedProfile === 0)) {
      return false;
    }
    // Otherwise, compare the values
    return currentProfile !== savedProfile;
  };

  // Create options array with "No Profile" option at the top
  const profileOptions = [
    {
      value: null,
      label: (
        <div className="flex items-center gap-2">
          <Ban className="h-3 w-3 text-gray-500" />
          <span>No Profile</span>
        </div>
      )
    },
    ...profilesApiData.map(v => ({ value: v.id, label: v.name }))
  ];

  return (
    <div className="flex flex-col gap-3">

      <div className="flex justify-start items-center mb-2">
        <h2 className="heading-6 text-dark-blue">Calendar</h2>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {/* Weekday Settings */}
        <div className="flex flex-col gap-2.5 bg-white rounded-[10px] shadow-card p-2.5">
          <h3 className="heading-7 text-dark-blue">Weekday</h3>

          <div className="flex flex-col gap-2 py-2">
            <label className="paragraph-10 text-black">Select Profile</label>
            <div className="relative">
              <Select className="w-full"
                value={weekdayProfile}
                onChange={(e) => setWeekdayProfile(e)}
                options={profileOptions}
                placeholder="Select a profile"
              />
            </div>
          </div>
          <div className="flex">
            <Button
              size="md"
              className="bg-primary text-white"
              disabled={!isProfileDifferent(weekdayProfile, calendarApiData?.[0]?.weekday_profile)}
              onClick={() => handleSave(weekdayProfile, true)}
            >
              <p className="text-button-s">Save</p>
            </Button>
          </div>
        </div>

        {/* Weekend Settings */}
        <div className="flex flex-col gap-2.5 bg-white rounded-[10px] shadow-card p-2.5">
          <h3 className="heading-7 text-dark-blue">Weekend</h3>

          <div className="flex flex-col gap-2 py-2">
            <label className="paragraph-10 text-black">Select Profile</label>
            <div className="relative">
              <Select className="w-full"
                value={weekendProfile}
                onChange={(e) => setWeekendProfile(e)}
                options={profileOptions}
                placeholder="Select a profile"
              />
            </div>
          </div>
          <div className="flex">
            <Button
              size="md"
              className="bg-primary text-white"
              disabled={!isProfileDifferent(weekendProfile, calendarApiData?.[0]?.weekend_profile)}
              onClick={() => handleSave(weekendProfile, false)}
            >
              <p className="text-button-s">Save</p>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}